<template>
  <div
    class="flex flex-col p-2 rounded-xl"
    :class="{
      'bg-slate-300': target.target_tasks && target.target_tasks.length > 0
    }"
  >
    <div
      class="bl-target"
      :class="[
        active && !target.target_tasks ? 'bl-target-active' : '',
        active && !activeTask > 0 ? 'bl-target-active' : '',
        target.target_tasks && target.target_tasks.length > 0 ? 'mb-2' : ''
      ]"
    >
      <div
        :style="{ backgroundColor: target.curriculum_color || '#EAECF0' }"
        class="pointer-events-none bl-target-border"
      />
      <div
        class="bl-target-container bg-pure-white"
        :class="[useAction ? 'hover:border-light-purple cursor-pointer' : '']"
        @click.prevent="$emit('clicked', target.id)"
      >
        <div class="bl-target-header">
          <BlTextBody2
            class="truncate bl-target-description"
            :text="target.curriculum_name"
          />
          <div class="bl-target-actions">
            <div
              v-if="
                checkPassedActionRecommendation(target) &&
                  target.status !== 'mastered'
              "
              class="p-1 bg-orange-300"
              :style="{ borderRadius: '4px' }"
            >
              <BlIcon icon="tooltip" class="w-4 h-4" />
            </div>
            <StatusChip
              v-if="target.status"
              :status="target.status"
              class="text-xs"
            />
            <BlInputButton
              v-if="useAction"
              @clicked="opened = true"
              icon="dots-vertical"
              class="flex items-center justify-center w-6 h-6 hover:bg-hover-state"
              type="plain"
            />
          </div>
        </div>
        <div class="mt-2 bl-target-body">
          <BlTextSubtitle2
            class="truncate bl-target-name"
            :text="target.name"
          />
        </div>
      </div>
      <div v-if="opened" class="bl-target-dropdown" :data-id="id">
        <div class="bl-target-dropdown-body" :data-id="id">
          <div
            v-for="(option, i) in options"
            :key="i"
            class="bl-target-dropdown-option"
            :data-id="id"
            @click="$emit(option.event)"
          >
            <BlIcon
              class="bl-target-dropdown-option-icon"
              :icon="option.icon"
              :color="option.type || 'default'"
            />
            <span
              class="bl-target-dropdown-option-name"
              :class="`bl-target-dropdown-option-name-${option.type}`"
            >
              {{ option.name }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="target.target_tasks && target.target_tasks.length > 0"
      class="flex flex-col gap-3"
    >
      <div
        v-for="(item, i) in target.target_tasks"
        :key="item.id"
        class="flex items-center gap-3"
      >
        <div
          class="flex items-center justify-center flex-shrink-0 w-2 h-2 text-sm font-medium rounded-full top-2 -left-12 bg-slate-600"
        >
          <div class="relative flex flex-col items-center justify-center">
            <div
              v-if="i !== target.target_tasks.length - 1"
              class="absolute transform h-9 top-2 bg-slate-600"
              style="width: 1px;"
            />
          </div>
        </div>

        <div
          class="flex items-center justify-between w-full p-2 overflow-hidden rounded bg-pure-white"
          :class="{
            'cursor-pointer': !isFromSession,
            'border border-light-purple':
              activeTask && activeTask.id === item.id,
            'border border-pure-white': !activeTask || activeTask.id !== item.id
          }"
          @click.prevent="$emit('clickedTask', item, target.id)"
        >
            <BlTextSubtitle3
              :text="`${item.code} - ${item.title}`"
              class="truncate text-slate-1000"
            />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StatusChip from "./StatusChip.vue";

import BlIcon from "../../BlIcon.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";

export default {
  name: "TargetCard",
  components: {
    StatusChip,
    BlTextSubtitle2,
    BlTextBody2,
    BlInputButton,
    BlIcon,
    BlTextSubtitle3
  },
  props: {
    target: {
      type: Object,
      required: true
    },
    active: {
      type: Boolean,
      default: false
    },
    useAction: {
      type: Boolean,
      default: false
    },
    activeTask: {
      type: Object,
      default: null
    },
    isFromSession: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      opened: false,
      options: [
        {
          name: "Delete target",
          type: "error",
          icon: "trash",
          event: "delete"
        }
      ]
    };
  },
  computed: {
    id() {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      let combination = "";
      for (let i = 0; i < 6; i++) {
        combination += chars[Math.round(Math.random() * 35)];
      }
      return `bl-component-${combination}`;
    }
  },
  watch: {
    opened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandler);
        }, 10);
      }
    }
  },
  methods: {
    closeHandler() {
      window.removeEventListener("click", this.closeHandler);
      this.opened = false;
    },
    checkPassedActionRecommendation(target) {
      const arr = target.action_recommendations || [];
      const idx = arr.findIndex(i => i.visible && i.passed);
      return idx > -1;
    }
  }
};
</script>

<style scoped lang="scss">
.bl-target {
  position: relative;
  width: 100%;
  border-radius: 5px;
  display: flex;

  .bl-target-border {
    pointer-events: none;
    position: relative;
    width: 6px;
    height: auto;
    max-height: 100%;
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
  }

  .bl-target-container {
    position: relative;
    width: calc(100% - 6px);
    padding: 1rem;
    border: 1px solid var(--border-color);
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
    border-left: none;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;

    .bl-target-header {
      position: relative;
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 0.5rem;

      .bl-target-actions {
        position: relative;
        display: flex;
        flex-shrink: 0;
        align-items: center;
        gap: 0.5rem;

        .bl-target-status {
          position: relative;
          height: 1.5rem;
          padding: 0 0.5rem;
          border-radius: 0.75rem;
          font-size: 0.75rem;
          font-weight: 400;
          letter-spacing: 0.5px;
          display: flex;
          justify-content: center;
          align-items: center;
        }
      }
    }

    .bl-target-body {
      position: relative;
      width: 100%;
      display: flex;
      flex-direction: column;

      .bl-target-description {
        opacity: 0.5;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        line-clamp: 2;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }
    }
  }

  .bl-target-dropdown {
    z-index: 99;
    position: absolute;
    top: 3.25rem;
    right: 0.25rem;
    background: var(--background-depth-one-color);
    border-radius: 5px;
    border: 1px solid var(--border-color);
    box-shadow: 0 0.5rem 1rem rgba(100, 100, 100, 0.05);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;

    .bl-target-dropdown-body {
      position: relative;
      width: 100%;
      max-height: 13.75rem;
      padding: 0.5rem 1rem;
      display: flex;
      gap: 0.5rem;
      flex-direction: column;
      overflow-y: auto;

      .bl-target-dropdown-search {
        position: relative;
        height: 2.5rem;
        flex-shrink: 0;
      }

      .bl-target-dropdown-option {
        cursor: pointer;
        position: relative;
        width: 100%;
        height: 2.5rem;
        display: flex;
        flex-shrink: 0;
        gap: 0.75rem;
        align-items: center;

        .bl-target-dropdown-option-icon {
          pointer-events: none;
          position: relative;
          width: 1.5rem;
          height: 1.5rem;
        }

        .bl-target-dropdown-option-name {
          pointer-events: none;
          position: relative;
          font-size: 0.75rem;
          font-weight: 400;
          line-height: calc(5 / 3);
          letter-spacing: 0.5px;

          &.bl-target-dropdown-option-name-error {
            color: var(--error-color);
          }
        }
      }
    }
  }

  &.bl-target-active {
    .bl-target-container {
      border-color: var(--primary-color);
      border-width: 2px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/clients/target/Card.vue